// ============================================================================
// ENTRÉE EN CONNEXION D'UNE SURFACE WEB FÉDÉRÉE — la séquence, en un seul endroit.
//
// ⚠️ POURQUOI PARTAGÉ : les trois consoles (Console, store privé, EDI) sont des RP du même IdP. Chacune
// avait réimplémenté « où envoyer quelqu'un qui n'est pas connecté », et les trois avaient DIVERGÉ —
// l'EDI affichait son formulaire local alors que l'utilisateur avait une session Orgazone ouverte, et
// deux d'entre elles perdaient la page demandée. Une connexion unique qui redemande de cliquer n'est
// pas une connexion unique. Quatre défauts en une journée venaient de mécaniques recopiées : celle-ci
// ne l'est plus.
//
// Ce que ce module NE décide PAS : la politique d'accès (qui a le droit d'entrer) et la fraîcheur
// d'authentification exigée — elles vivent côté serveur et diffèrent légitimement par application.
// ============================================================================

/**
 * Une session a-t-elle été établie depuis le chargement de la page ?
 *
 * ⚠️ C'est ce qui distingue « ma session a expiré » de « je n'ai jamais été connecté ». Le store sert
 * des pages PUBLIQUES (catalogue, fiche d'application) : sans cette distinction, un visiteur anonyme
 * serait expédié vers le SSO au premier appel refusé, ce qui transformerait une vitrine publique en
 * porte fermée.
 */
let sessionEtablie = false;

/** À appeler quand la session est résolue et VALIDE (typiquement après un `/auth/me` réussi). */
export const markSessionEstablished = (): void => { sessionEtablie = true; };

/**
 * Destination à rejoindre après authentification : celle mise de côté par un rebond (`?next=`), sinon
 * la page courante.
 *
 * ⚠️ Lire `?next=` d'abord n'est pas un détail : sans cela, un rebond depuis `/?next=/journal`
 * demanderait à revenir sur `/?next=/journal` lui-même, et l'utilisateur tournerait en rond.
 */
export function ssoTarget(): string {
  const demandee = new URLSearchParams(window.location.search).get('next');
  return demandee ?? window.location.pathname + window.location.search;
}

/**
 * Part se connecter en SSO.
 *
 * ⚠️ NAVIGATION PLEINE PAGE : `/v1/auth/oidc/login` est une route SERVEUR, pas une route du SPA — elle
 * pose le cookie d'état PKCE avant de partir chez l'IdP. Y aller par le routeur ne ferait rien.
 */
export function goToSsoLogin(next: string = ssoTarget()): void {
  window.location.href = `/v1/auth/oidc/login?next=${encodeURIComponent(next)}`;
}

let rebondEnCours = false;

/**
 * Politique 401 commune : une session perdue en cours d'usage renvoie se connecter, en gardant la page.
 *
 * ⚠️ NE FAIT RIEN si aucune session n'a été établie — voir `sessionEtablie`. Et ne fait rien deux fois :
 * plusieurs appels peuvent échouer ensemble au retour d'une veille, et autant de navigations
 * concurrentes se voleraient la destination.
 *
 * ⚠️ Passe par la RACINE et non par `/v1/auth/oidc/login` directement : c'est la garde de route de
 * l'application qui doit décider (SSO si l'IdP est configuré, écran local de bris de glace sinon).
 * Court-circuiter la garde, c'est réintroduire la divergence que ce module supprime.
 */
export function bounceOnUnauthorized(): void {
  if (!sessionEtablie || rebondEnCours) return;
  rebondEnCours = true;
  window.location.assign(`/?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
}
